// Decoded, validated land-navigation content (taxgw-2ybg B1). The offline baker (tools/data/land-navigation.mjs)
// writes a sparse document of one rhumb corridor per adjacent city pair; this loader is the ONE decoder, validated
// once at module load so bad data throws ContentValidationError (docs/ARCHITECTURE.md §13) and the game refuses to
// start.
//
// Canonical city coordinates are cities.json's fact (geo.ts / ALL_CITIES); the document must not carry a second
// lat/lon table. Here every lane endpoint is checked against the canonical position modulo an integer 360° chart
// shift, so an unwrapped antimeridian leg is accepted while a genuinely wrong coordinate is not.
//
// This module imports JSON and the geo dataset, so it is NOT a pure geometry subpath: the Node data tool imports
// only @taxwar/content/geometry/*. The runtime prepares PreparedPath geometry and the CSR library separately
// (sim/pathing/land-library.ts), deriving every cost from the shared geometry rather than from artifact fields.

import { MAX_BAKED_CITIES, MAX_BAKED_CONTROL_POINTS, MAX_MEAN_LANES_PER_CITY, MAX_NAVIGATION_DOCUMENT_BYTES, NAVIGATION_FORMAT_VERSION } from './geometry/navigation-format'
import { InvariantViolation } from './geometry/invariants'
import { PreparedPath, sameEndpoint, surfacePathStrategy, type LatLon, type SurfacePathKind } from './geometry/routes'
import { ContentValidationError } from './errors'
import { ALL_CITIES, CITY_BY_ID, type City, type CityId } from './geo'
import landNavigationJson from '../data/land-navigation.json'

/** One baked navigation site: a shipped city attached to the land mesh and its land component. */
export interface LandNavigationSite {
  readonly id: CityId
  readonly component: number
}

/** One baked land corridor between two adjacent sites; `points` is a rhumb polyline with unwrapped longitudes. */
export interface LandNavigationLane {
  readonly a: CityId
  readonly b: CityId
  readonly points: readonly LatLon[]
}

export interface LandNavigation {
  /** Serialized format version (NAVIGATION_FORMAT_VERSION). */
  readonly version: number
  /** Shared surface-path kind of every lane. */
  readonly pathKind: SurfacePathKind
  /** Every shipped city, ascending by CityId. */
  readonly sites: readonly LandNavigationSite[]
  /** One corridor per unordered site pair, strictly ascending by (a, b). */
  readonly lanes: readonly LandNavigationLane[]
}

function fail(message: string): never {
  // One action for every content fault: the offline document is wrong, so it must be regenerated, not patched.
  throw new ContentValidationError(`land navigation: ${message}; regenerate the land-navigation content by running npm run data:build`)
}

/**
 * UTF-8 byte length of a string, counted from its UTF-16 code units. Content is ES2020-only like `net` and `sim` (no
 * DOM or platform encoder), so the count is done here; a lone surrogate counts as the 3-byte replacement character.
 */
function utf8ByteLength(text: string): number {
  let bytes = 0
  for (let i = 0; i < text.length; i++) {
    const unit = text.charCodeAt(i)
    if (unit < 0x80) bytes += 1
    else if (unit < 0x800) bytes += 2
    else if (unit >= 0xd800 && unit <= 0xdbff && i + 1 < text.length && text.charCodeAt(i + 1) >= 0xdc00 && text.charCodeAt(i + 1) <= 0xdfff) {
      bytes += 4
      i++
    } else bytes += 3
  }
  return bytes
}

/** Admits the serialized path kind through the ONE strategy registry, not a hand-grown list of kind names. */
function admitPathKind(rawKind: unknown): SurfacePathKind {
  if (typeof rawKind !== 'string') fail(`path kind ${String(rawKind)} is not a string`)
  try {
    surfacePathStrategy(rawKind as SurfacePathKind)
  } catch (error) {
    if (error instanceof InvariantViolation) fail(`unknown path kind ${String(rawKind)}`)
    throw error
  }
  return rawKind as SurfacePathKind
}

/** Union-find over dense site indices, used to prove each component's lanes connect exactly its own cities. */
class SiteGroups {
  private readonly parent: Int32Array

  constructor(size: number) {
    this.parent = new Int32Array(size)
    for (let i = 0; i < size; i++) this.parent[i] = i
  }

  find(value: number): number {
    let node = value
    for (;;) {
      const up = this.parent[node] as number
      if (up === node) return node
      const grandparent = this.parent[up] as number
      this.parent[node] = grandparent
      node = grandparent
    }
  }

  union(a: number, b: number): void {
    const rootA = this.find(a)
    const rootB = this.find(b)
    if (rootA !== rootB) this.parent[Math.max(rootA, rootB)] = Math.min(rootA, rootB)
  }
}

function loadLandNavigation(): LandNavigation {
  // The compact serialized JSON is a stand-in for the shipped file; the real file-byte budget is enforced at the
  // producer (tools/data/build.mjs) against the actual serialized buffer. The string is a bounded temporary.
  const documentBytes = utf8ByteLength(JSON.stringify(landNavigationJson))
  if (documentBytes > MAX_NAVIGATION_DOCUMENT_BYTES) {
    fail(`the compact serialized document is ${documentBytes} bytes, over the ${MAX_NAVIGATION_DOCUMENT_BYTES}-byte budget`)
  }

  const rawVersion = landNavigationJson.version
  if (rawVersion !== NAVIGATION_FORMAT_VERSION) fail(`version ${rawVersion}, expected ${NAVIGATION_FORMAT_VERSION}`)
  const pathKind = admitPathKind(landNavigationJson.pathKind)

  const rawSites = landNavigationJson.cities
  if (!Array.isArray(rawSites)) fail('cities is not an array')
  if (rawSites.length > MAX_BAKED_CITIES) fail(`${rawSites.length} sites exceed the ${MAX_BAKED_CITIES} city budget`)
  if (rawSites.length === 0) fail('no sites')

  const sites: LandNavigationSite[] = []
  const indexById = new Map<number, number>()
  const componentLabels = new Set<number>()
  let highestComponent = -1
  let previousSiteId = -1
  for (const rawSite of rawSites) {
    if (typeof rawSite.id !== 'number' || !Number.isInteger(rawSite.id)) fail(`site id ${String(rawSite.id)} is not an integer CityId`)
    // The document claims ascending site order; enforce it rather than trusting the claim.
    if (rawSite.id <= previousSiteId) fail(`sites are not strictly ascending by CityId at ${rawSite.id}`)
    previousSiteId = rawSite.id
    if (!CITY_BY_ID.has(rawSite.id as CityId)) fail(`site ${rawSite.id} is not a shipped city`)
    if (!Number.isInteger(rawSite.component) || rawSite.component < 0) fail(`site ${rawSite.id} has invalid component ${rawSite.component}`)
    indexById.set(rawSite.id, sites.length)
    sites.push(Object.freeze({ id: rawSite.id as CityId, component: rawSite.component }))
    componentLabels.add(rawSite.component)
    if (rawSite.component > highestComponent) highestComponent = rawSite.component
  }
  if (sites.length !== ALL_CITIES.length) fail(`${sites.length} sites for ${ALL_CITIES.length} shipped cities`)
  if (componentLabels.size !== highestComponent + 1) fail(`component labels are not contiguous 0…${highestComponent}`)

  const rawLanes = landNavigationJson.lanes
  if (!Array.isArray(rawLanes)) fail('lanes is not an array')
  if (rawLanes.length > MAX_MEAN_LANES_PER_CITY * sites.length) fail(`${rawLanes.length} lanes exceed the ${MAX_MEAN_LANES_PER_CITY}×${sites.length} lane budget`)
  const lanes: LandNavigationLane[] = []
  const groups = new SiteGroups(sites.length)
  let controlPoints = 0
  let previousA = -1
  let previousB = -1
  for (const rawLane of rawLanes) {
    const a = rawLane.a
    const b = rawLane.b
    if (!Number.isInteger(a) || !Number.isInteger(b) || !(a < b)) fail(`lane ${String(a)}-${String(b)} is not an ordered pair a < b`)
    if (!(a > previousA || (a === previousA && b > previousB))) fail(`lanes are not strictly ascending by (a, b) at ${a}-${b}`)
    previousA = a
    previousB = b
    const siteA = indexById.get(a)
    const siteB = indexById.get(b)
    if (siteA === undefined || siteB === undefined) fail(`lane ${a}-${b} names an unknown site`)
    const cityA = CITY_BY_ID.get(a as CityId) as City
    const cityB = CITY_BY_ID.get(b as CityId) as City
    if ((sites[siteA] as LandNavigationSite).component !== (sites[siteB] as LandNavigationSite).component) {
      fail(`lane ${a}-${b} bridges land components ${(sites[siteA] as LandNavigationSite).component} and ${(sites[siteB] as LandNavigationSite).component}`)
    }
    if (!Array.isArray(rawLane.points) || rawLane.points.length < 2) fail(`lane ${a}-${b} has fewer than two points`)
    const points: LatLon[] = []
    for (const rawPoint of rawLane.points) {
      if (!Array.isArray(rawPoint) || rawPoint.length !== 2) fail(`lane ${a}-${b} has a malformed point`)
      const lat = rawPoint[0]
      const lon = rawPoint[1]
      if (typeof lat !== 'number' || typeof lon !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lon)) fail(`lane ${a}-${b} has a non-finite coordinate`)
      const point: LatLon = [lat, lon]
      points.push(Object.freeze(point))
    }
    const firstCanonical: LatLon = [cityA.lat, cityA.lon]
    const lastCanonical: LatLon = [cityB.lat, cityB.lon]
    if (!sameEndpoint(points[0] as LatLon, firstCanonical)) fail(`lane ${a}-${b} first point does not match ${cityA.name}`)
    if (!sameEndpoint(points[points.length - 1] as LatLon, lastCanonical)) fail(`lane ${a}-${b} last point does not match ${cityB.name}`)
    const frozenPoints = Object.freeze(points)
    // Admit the geometry through the shared path validation: poles, degenerate legs and non-positive lengths fail here.
    try {
      new PreparedPath({ kind: pathKind, points: frozenPoints })
    } catch (error) {
      if (error instanceof InvariantViolation) fail(`lane ${a}-${b} has invalid geometry: ${error.message}`)
      throw error
    }
    controlPoints += frozenPoints.length
    if (controlPoints > MAX_BAKED_CONTROL_POINTS) fail(`more than ${MAX_BAKED_CONTROL_POINTS} lane control points`)
    groups.union(siteA, siteB)
    lanes.push(Object.freeze({ a: a as CityId, b: b as CityId, points: frozenPoints }))
  }

  // Component connectivity: every site of a component shares one lane group, and no group spans two components.
  const groupOfComponent = new Map<number, number>()
  const componentOfGroup = new Map<number, number>()
  for (let index = 0; index < sites.length; index++) {
    const component = (sites[index] as LandNavigationSite).component
    const root = groups.find(index)
    const knownRoot = groupOfComponent.get(component)
    if (knownRoot === undefined) groupOfComponent.set(component, root)
    else if (knownRoot !== root) fail(`land component ${component} is split across disconnected lane groups`)
    const knownComponent = componentOfGroup.get(root)
    if (knownComponent === undefined) componentOfGroup.set(root, component)
    else if (knownComponent !== component) fail(`lane group ${root} spans land components ${knownComponent} and ${component}`)
  }

  return Object.freeze({ version: NAVIGATION_FORMAT_VERSION, pathKind, sites: Object.freeze(sites), lanes: Object.freeze(lanes) })
}

/** The bundled, validated land-navigation document. */
export const LAND_NAVIGATION: LandNavigation = loadLandNavigation()
